// Kizuna Link bench page (docs/81 milestone M0): connect a GB-Link, read its firmware,
// start cable or wireless mode and log what the device reports. No relay yet.

import { Cmd, Mode, ModeId, Status, containsRfu1Magic, formatVersion, statusName, versionAtLeast } from "./gblink";
import { ClaimError, GbLinkDevice, GbLinkEvents } from "./usb";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const els = {
  unsupported: $("unsupported"),
  connect: $<HTMLButtonElement>("connect"),
  disconnect: $<HTMLButtonElement>("disconnect"),
  deviceLine: $("device-line"),
  fwLine: $("fw-line"),
  modeButtons: [...document.querySelectorAll<HTMLButtonElement>("[data-mode]")],
  cmdButtons: [...document.querySelectorAll<HTMLButtonElement>("[data-cmd]")],
  state: $("state"),
  counters: $("counters"),
  log: $("log"),
  showFrames: $<HTMLInputElement>("show-frames"),
  clear: $<HTMLButtonElement>("clear"),
  copy: $<HTMLButtonElement>("copy"),
};

let dev: GbLinkDevice | null = null;
let currentMode: ModeId | null = null;
const counts = { linkIn: 0, rfu1In: 0, shortIn: 0 };

const hex = (b: Uint8Array) => [...b].map((x) => x.toString(16).padStart(2, "0")).join(" ");
const words = (b: Uint8Array) => {
  const v = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const out: string[] = [];
  for (let i = 0; i + 1 < b.length; i += 2) out.push(v.getUint16(i, true).toString(16).padStart(4, "0"));
  return out.join(" ");
};

function log(kind: "info" | "status" | "data" | "warn" | "error", text: string): void {
  const line = document.createElement("div");
  line.className = `log-${kind}`;
  const t = new Date();
  const ts = `${t.toLocaleTimeString([], { hour12: false })}.${String(t.getMilliseconds()).padStart(3, "0")}`;
  line.textContent = `${ts}  ${text}`;
  els.log.append(line);
  while (els.log.childElementCount > 2000) els.log.firstElementChild?.remove();
  els.log.scrollTop = els.log.scrollHeight;
}

function setState(text: string): void {
  els.state.textContent = text;
}

function renderCounters(): void {
  els.counters.textContent =
    `64-byte link frames in: ${counts.linkIn}` +
    (currentMode === Mode.WirelessAdapter ? ` (with RFU1 magic: ${counts.rfu1In})` : "") +
    ` · short frames in: ${counts.shortIn}`;
}

function setConnected(on: boolean): void {
  els.connect.hidden = on;
  els.disconnect.hidden = !on;
  els.modeButtons.forEach((b) => (b.disabled = !on));
  els.cmdButtons.forEach((b) => (b.disabled = !on));
  if (!on) {
    els.deviceLine.textContent = "No device connected.";
    els.fwLine.textContent = "";
    currentMode = null;
    setState("Idle");
  }
}

const events: GbLinkEvents = {
  status(code) {
    log("status", `status ${statusName(code)} (0x${code.toString(16)})`);
    if (code === Status.AwaitMode) setState("Waiting for a partner (AwaitMode). The relay would pick master/slave now.");
    else if (code === Status.HandshakeReceived) setState("Game Boy handshake seen (HandshakeReceived).");
    else if (code === Status.LinkConnected) setState("Link connected.");
    else if (code === Status.LinkClosed) setState("Link closed.");
    else if (code === Status.LinkReconnecting) setState("Reconnecting…");
  },
  linkData(frame) {
    counts.linkIn++;
    if (currentMode === Mode.WirelessAdapter && containsRfu1Magic(frame)) counts.rfu1In++;
    renderCounters();
    if (els.showFrames.checked) {
      log("data", currentMode === Mode.GbaLink ? `data in  ${words(frame)}` : `data in  ${hex(frame)}`);
    }
  },
  shortData(bytes) {
    counts.shortIn++;
    renderCounters();
    if (els.showFrames.checked) log("data", `short in (${bytes.length} B) ${hex(bytes)}`);
  },
  disconnected(reason) {
    log("warn", `device disconnected: ${reason}`);
    dev = null;
    setConnected(false);
  },
};

async function afterOpen(d: GbLinkDevice): Promise<void> {
  dev = d;
  setConnected(true);
  els.deviceLine.textContent = `Connected: ${d.productName}`;
  log("info", `opened ${d.productName}`);
  try {
    const fw = await d.firmwareInfo();
    const v = formatVersion(fw);
    let note = "";
    if (!versionAtLeast(fw, 2, 2, 6)) note = " - too old for the wireless adapter; update at gblink.io";
    else if (v !== "2.2.7") note = " - not the tested version (2.2.7)";
    els.fwLine.textContent = `Firmware ${v}${note}`;
    log("info", `firmware ${v}`);
  } catch (e) {
    els.fwLine.textContent = "Firmware version unknown (no reply).";
    log("warn", `firmware info: ${(e as Error).message}`);
  }
}

els.connect.addEventListener("click", async () => {
  try {
    await afterOpen(await GbLinkDevice.request(events));
  } catch (e) {
    if (e instanceof DOMException && e.name === "NotFoundError") return; // user closed the picker
    log("error", e instanceof ClaimError ? e.message : `connect failed: ${(e as Error).message}`);
  }
});

els.disconnect.addEventListener("click", async () => {
  await dev?.close();
  dev = null;
  setConnected(false);
  log("info", "disconnected (sent Cancel)");
});

els.modeButtons.forEach((b) =>
  b.addEventListener("click", async () => {
    if (!dev) return;
    const mode = Number(b.dataset.mode) as ModeId;
    const variant = b.dataset.variant === undefined ? undefined : Number(b.dataset.variant);
    currentMode = mode;
    counts.linkIn = counts.rfu1In = counts.shortIn = 0;
    renderCounters();
    setState("Starting…");
    log("info", `start ${b.textContent?.trim()}: Cancel, wait 500 ms, 3.3 V, SetMode 0x0${mode}`);
    try {
      await dev.startMode(mode, variant);
      log("info", "DeviceReady received");
      setState("Device ready, waiting for its first status…");
    } catch (e) {
      setState("Start failed.");
      log("error", `start failed: ${(e as Error).message}`);
    }
  }),
);

const CMD_LABELS: Record<number, string> = {
  [Cmd.SetModeMaster]: "SetModeMaster",
  [Cmd.SetModeSlave]: "SetModeSlave",
  [Cmd.StartHandshake]: "StartHandshake",
  [Cmd.ConnectLink]: "ConnectLink",
  [Cmd.Cancel]: "Cancel",
};

els.cmdButtons.forEach((b) =>
  b.addEventListener("click", async () => {
    if (!dev) return;
    const c = Number(b.dataset.cmd);
    try {
      await dev.command(c);
      log("info", `sent ${CMD_LABELS[c] ?? c}`);
    } catch (e) {
      log("error", `command failed: ${(e as Error).message}`);
    }
  }),
);

els.clear.addEventListener("click", () => (els.log.textContent = ""));
els.copy.addEventListener("click", async () => {
  await navigator.clipboard.writeText(els.log.innerText);
  els.copy.textContent = "Copied";
  setTimeout(() => (els.copy.textContent = "Copy log"), 1200);
});

// Best effort: leave the device idle when the tab goes away.
addEventListener("pagehide", () => void dev?.cancel().catch(() => {}));

if (!GbLinkDevice.isSupported()) {
  els.unsupported.hidden = false;
  els.connect.disabled = true;
} else {
  setConnected(false);
  navigator.usb.addEventListener("disconnect", (e) => {
    if (dev && e.device === dev.usb) events.disconnected("unplugged");
  });
  GbLinkDevice.reopen(events)
    .then((d) => d && afterOpen(d))
    .catch((e) => log("warn", e instanceof ClaimError ? e.message : `auto-connect: ${(e as Error).message}`));
}
